import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Layout
import DashboardLayout from './components/layout/DashboardLayout';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import TemplatesPage from './pages/TemplatesPage';
import InboxPage from './pages/InboxPage';
import TeamPage from './pages/TeamPage';
import SettingsPage from './pages/SettingsPage';
import AdminClientsPage from './pages/AdminClientsPage';
import AdminActivityPage from './pages/AdminActivityPage';

// Protected Route for Super Admin
const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return null;
  if (!user || user.role !== 'SUPER_ADMIN') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Protected Route for Client/Team Member
const ClientRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user || user.role === 'SUPER_ADMIN') {
    return <Navigate to="/admin/clients" replace />;
  }
  return children;
};

// Protected Route — blocks TEAM_MEMBER role
const NonTeamMemberRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (user?.role === 'TEAM_MEMBER') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Root redirect based on role
const RootRedirect = () => {
  const { user, loading, isAuthenticated } = useAuth();
  
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user.role === 'SUPER_ADMIN') return <Navigate to="/admin/clients" replace />;
  return <Navigate to="/dashboard" replace />;
};

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#131517] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <RootRedirect />} />
      
      {/* Dashboard Routes */}
      <Route element={<DashboardLayout />}>
        {/* Client Routes */}
        <Route path="/dashboard" element={<ClientRoute><DashboardPage /></ClientRoute>} />
        <Route path="/templates" element={<ClientRoute><TemplatesPage /></ClientRoute>} />
        <Route path="/inbox" element={<ClientRoute><InboxPage /></ClientRoute>} />
        <Route path="/team" element={<ClientRoute><NonTeamMemberRoute><TeamPage /></NonTeamMemberRoute></ClientRoute>} />
        <Route path="/settings" element={<ClientRoute><SettingsPage /></ClientRoute>} />
        
        {/* Super Admin Routes */}
        <Route path="/admin/clients" element={<SuperAdminRoute><AdminClientsPage /></SuperAdminRoute>} />
        <Route path="/admin/activity" element={<SuperAdminRoute><AdminActivityPage /></SuperAdminRoute>} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
